import ms from 'ms';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { STATUSES } from '@guuru/chat-common';
import {
  CHAT_INPUT_MODES,
  CHAT_VIEW_STATES,
  useChatMessagesSender,
  useIsVisible,
  useSoundEffect,
} from '@guuru/chat-web';
import { ChatMessages, ChatRowWrapper } from '@guuru/react-chat-messages';
import { destroy, loading, warning } from '@guuru/react-message';
import { useAttachmentSubmit } from '@guuru/react-upload-hooks';
import ChatDropActive from './ChatDropActive';
import ChatHeader from './ChatHeader';
import ChatHeaderWrapper from './ChatHeaderWrapper';
import ChatNewMessagesCta from './ChatNewMessagesCta';
import ChatRateCta from './ChatRateCta';
import RateSection from './ChatRateSection';
import TermsAndConditions from './ChatTermsAndConditions';
import ChatThankYouSection from './ChatThankYouSection';
import useCanRate from './hooks/useCanRate';
import useChatMessages from './hooks/useChatMessages';
import useScrollToBottom from './hooks/useScrollToBottom';
import { ChatScrollAnchor, ChatView } from './style';

const { title } = document;
const favicon = {
  normal: 'https://cdn.guuru.com/assets/logo/favicon.ico',
  unread: 'https://cdn.guuru.com/assets/logo/favicon_dot.png',
};

const ChatWidget = function ({
  children,
  renderChatMessageBar,
  renderChatHeader,
  chatId = null,
  showTermsAndConditions = false,
  isUser = false,
  localMessages = [],
  viewState = null,
  inputMode = null,
  showBotAvatar = true,
  customBotAvatar = null,
  baseMods = 'mod--chat',
  onRunNextStep = () => {},
  onSubmitChatText = () => {},
  alert = null,
}) {
  const {
    status,
    expert,
    partner,
    messages,
    acceptedAt,
    rating,
    unreadMessagesCount,
    markMessagesRead,
    incrementUnreadMessages,
  } = useChatMessages({
    chatId,
    isUser,
    localMessages,
  });
  const { sendAttachment } = useChatMessagesSender({ chatId, isUser });

  const { scrollToBottom, isNearBottom, setContainerRef, setScrollAnchorRef } =
    useScrollToBottom();

  const isVisible = useIsVisible();
  const isLastMessageHidden = !isVisible || !isNearBottom;
  useSoundEffect({ when: unreadMessagesCount > 0 && isLastMessageHidden }, [
    isLastMessageHidden,
    unreadMessagesCount,
  ]);

  useEffect(() => {
    if (isNearBottom && unreadMessagesCount > 0 && isVisible) {
      markMessagesRead();
    }
  }, [isNearBottom, markMessagesRead, unreadMessagesCount, isVisible]);

  const handleAttachmentSubmit = useAttachmentSubmit({
    chatId,
    onStart: scrollToBottom,
    onUpload: sendAttachment,
    onProgress: loading,
    onError: warning,
    onFinish: destroy,
  });

  const canRateData = {
    messages,
    acceptedAt,
    expert,
    isUser,
    isOpen:
      viewState === CHAT_VIEW_STATES.chat &&
      inputMode !== CHAT_INPUT_MODES.none,
  };

  const canRateHeader = useCanRate({
    ...canRateData,
    promptTimer: ms('1.5m'),
    afterUserReplied: true,
  });

  const canRateBottom = useCanRate({
    ...canRateData,
    promptTimer: ms('4m'),
    totalExpertMessages: 3,
    totalExpertCharacters: 250,
  });

  const onDrop = useCallback(
    ([file]) => {
      handleAttachmentSubmit({ target: { files: [file] } });
    },
    [handleAttachmentSubmit],
  );

  const mods = useMemo(
    () => (isUser ? `${baseMods} mod--user` : `${baseMods} mod--expert`),
    [baseMods, isUser],
  );

  const currentTitle = unreadMessagesCount
    ? `(${unreadMessagesCount <= 9 ? unreadMessagesCount : '9+'}) ${title}`
    : title;

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    disabled: !['created', 'assigned', 'open'].includes(status),
    onDrop,
  });

  return (
    <ChatView className={mods} {...getRootProps()}>
      <title>{currentTitle}</title>
      <link
        rel="shortcut icon"
        href={unreadMessagesCount ? favicon.unread : favicon.normal}
      />
      <ChatHeaderWrapper
        isUser={isUser}
        header={
          renderChatHeader ? (
            renderChatHeader()
          ) : (
            <ChatHeader chatId={chatId} showRate={canRateHeader} />
          )
        }
      />
      <ChatDropActive isDragActive={isDragActive} />
      <ChatRowWrapper>{alert}</ChatRowWrapper>
      <main
        aria-label="Chat messages"
        ref={setContainerRef}
        className={`chat-content-section ${mods}`}
      >
        <TermsAndConditions visible={showTermsAndConditions} />
        <ChatMessages
          chatId={chatId}
          isUser={isUser}
          messages={messages}
          showBotAvatar={showBotAvatar}
          customBotAvatar={customBotAvatar}
          mods={mods}
          partner={partner}
          onRunNextStep={onRunNextStep}
          onSubmitChatText={onSubmitChatText}
        />
        <RateSection
          isUser={isUser}
          doBefore={() => incrementUnreadMessages()}
        />
        <ChatThankYouSection
          isUser={isUser}
          expert={expert}
          doBefore={() => incrementUnreadMessages()}
        />
        <ChatScrollAnchor
          id="chat-scroll-anchor"
          ref={setScrollAnchorRef}
          $setBackgroundColor={status === STATUSES.closed}
        />
      </main>
      {!isNearBottom && unreadMessagesCount > 0 && (
        <ChatNewMessagesCta
          count={unreadMessagesCount}
          onClick={scrollToBottom}
        />
      )}
      {children}
      {canRateBottom && isNearBottom && partner?.id && (
        <ChatRateCta
          partner={partner}
          chatId={chatId}
          rating={rating}
          expert={expert}
        />
      )}
      {renderChatMessageBar({
        onAttachmentSubmit: handleAttachmentSubmit,
        onMessageSubmit: scrollToBottom,
      })}
    </ChatView>
  );
};

ChatWidget.propTypes = {
  showTermsAndConditions: PropTypes.bool,
  children: PropTypes.node.isRequired,
  isUser: PropTypes.bool,
  chatId: PropTypes.string,
  localMessages: PropTypes.arrayOf(PropTypes.shape({})),
  viewState: PropTypes.string,
  inputMode: PropTypes.string,
  showBotAvatar: PropTypes.bool,
  customBotAvatar: PropTypes.string,
  baseMods: PropTypes.string,
  renderChatMessageBar: PropTypes.func.isRequired,
  renderChatHeader: PropTypes.func,
  onRunNextStep: PropTypes.func,
  onSubmitChatText: PropTypes.func,
  alert: PropTypes.node,
};

export default ChatWidget;
